import React, { ReactNode, useEffect, useState } from 'react'
import { Alert, Collapse, styled, useMediaQuery, useTheme } from '@mui/material'

import EnvironmentIndicator from '~components/common/EnvironmentIndicator'

import { BANNERS, BannerConfig } from './bannerConfig'
import './layout.less'
import Sidebar from './Sidebar'
import TopMenu from './TopMenu'

type PageProps = {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  padding?: number | string
  header?: React.ReactNode
  footer?: React.ReactNode
  hideMenu?: boolean
}

const TOP_MENU_HEIGHT = 64 // Adjust this value to match your TopMenu height

const Main = styled('main')<{
  isMobile: boolean
  customPadding: number | string
  hasHeader: boolean
}>(({ theme, isMobile, customPadding, hasHeader }) => ({
  flexGrow: 1,
  padding: customPadding,
  overflowY: 'auto',
  height: `calc(100vh - ${isMobile ? TOP_MENU_HEIGHT : 0}px - ${hasHeader ? '64px' : '0px'})`,
  paddingTop: isMobile ? `calc(${TOP_MENU_HEIGHT}px + ${customPadding})` : customPadding,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('md')]: {
    marginLeft: 0,
  },
}))

const PageContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}))

const HeaderWrapper = styled('header')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: theme.zIndex.appBar,
}))

const FooterWrapper = styled('footer')(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}))

const Page = ({
  children,
  className = '',
  style,
  padding,
  header,
  footer,
  hideMenu,
}: PageProps): ReactNode => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const customPadding = padding !== undefined ? padding : theme.spacing(3)
  const [activeBanners, setActiveBanners] = useState<BannerConfig[]>([])
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0)

  useEffect(() => {
    const now = new Date()

    const activeBannersList = BANNERS.filter((banner) => {
      const storageKey = `banner_${banner.id}`
      if (localStorage.getItem(storageKey)) {
        return false
      }

      if (banner.startDate) {
        const startDate = new Date(banner.startDate)
        if (now < startDate) {
          return false
        }
      }

      if (banner.endDate) {
        const endDate = new Date(banner.endDate)
        if (now > endDate) {
          return false
        }
      }

      return true
    })

    setActiveBanners(activeBannersList)
  }, [])

  const handleDismissBanner = (): void => {
    if (activeBanners.length === 0) return

    const currentBanner = activeBanners[currentBannerIndex]
    const storageKey = `banner_${currentBanner.id}`
    localStorage.setItem(storageKey, 'true')

    // Remove the current banner and update the index if needed
    const updatedBanners = activeBanners.filter((_, index) => index !== currentBannerIndex)
    setActiveBanners(updatedBanners)

    if (currentBannerIndex >= updatedBanners.length) {
      setCurrentBannerIndex(Math.max(0, updatedBanners.length - 1))
    }
  }

  const currentBanner = activeBanners[currentBannerIndex]

  const goToNextBanner = (): void => {
    if (activeBanners.length <= 1) return
    setCurrentBannerIndex((currentBannerIndex + 1) % activeBanners.length)
  }

  const goToPrevBanner = (): void => {
    if (activeBanners.length <= 1) return
    setCurrentBannerIndex((currentBannerIndex - 1 + activeBanners.length) % activeBanners.length)
  }

  const renderBannerContent = (banner: BannerConfig): ReactNode => {
    const { message, links } = banner

    if (!links || links.length === 0) {
      return message
    }

    let content = message as string
    const elements: ReactNode[] = []
    let lastIndex = 0

    links.forEach((link, i) => {
      const linkText = link.text
      const linkIndex = content.indexOf(linkText, lastIndex)

      if (linkIndex !== -1) {
        elements.push(content.substring(lastIndex, linkIndex))

        elements.push(
          <a
            key={i}
            href={link.url}
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {linkText}
          </a>,
        )

        lastIndex = linkIndex + linkText.length
      }
    })

    elements.push(content.substring(lastIndex))

    return elements
  }

  return (
    <>
      <EnvironmentIndicator position='top-right' />
      <PageContainer className={`page ${className}`} style={style}>
        {!hideMenu && (isMobile ? <TopMenu /> : <Sidebar />)}
        {header && <HeaderWrapper>{header}</HeaderWrapper>}
        <Main isMobile={isMobile} customPadding={customPadding} hasHeader={!!header}>
          {currentBanner && (
            <Collapse in={activeBanners.length > 0}>
              <Alert
                severity={currentBanner.severity}
                onClose={currentBanner.dismissible ? handleDismissBanner : undefined}
                icon={
                  currentBanner.customIcon
                    ? React.createElement(currentBanner.customIcon)
                    : undefined
                }
                sx={{
                  borderRadius: 0,
                  mb: 2,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    paddingRight: activeBanners.length > 1 ? '80px' : '0',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  {renderBannerContent(currentBanner)}
                </div>

                {activeBanners.length > 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 40,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      gap: '8px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      zIndex: 1,
                    }}
                  >
                    <span
                      onClick={goToPrevBanner}
                      style={{ cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}
                    >
                      ←
                    </span>
                    <span style={{ fontSize: '12px' }}>
                      {currentBannerIndex + 1}/{activeBanners.length}
                    </span>
                    <span
                      onClick={goToNextBanner}
                      style={{ cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}
                    >
                      →
                    </span>
                  </div>
                )}
              </Alert>
            </Collapse>
          )}
          <div style={{ minHeight: footer ? 'calc(100% - 60px)' : '100%' }}>{children}</div>
          {footer && <FooterWrapper>{footer}</FooterWrapper>}
        </Main>
      </PageContainer>
    </>
  )
}

export default Page
